/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useRef, useState } from "react";
import Container from "@/src/components/Container/Container";
import AndreasQuaukeImage from "@/src/assets/img/employees/Andreas_Quauke.jpg";
import AngelaFeilImage from "@/src/assets/img/employees/Angela_Feil.jpg";
import AnnaBeitenImage from "@/src/assets/img/employees/Anna_Beiten.jpg";
import ChristopherKrauseImage from "@/src/assets/img/employees/Christopher_Krause.jpg";
import DarioBorningImage from "@/src/assets/img/employees/Dario_Borning.jpg";
import ElenorGrubImage from "@/src/assets/img/employees/Elenor_Grub.jpg";
import FrankRiedelImage from "@/src/assets/img/employees/Frank_Riedel.jpg";
import FrederikSchierImage from "@/src/assets/img/employees/Frederik_Schier.jpg";
import HaraldHoevelerImage from "@/src/assets/img/employees/Harald_Hoeveler.jpg";
import KatharinaPitzenImage from "@/src/assets/img/employees/Katharina_Pitzen.jpg";
import KristinaBeyerImage from "@/src/assets/img/employees/Kristina_Beyer.jpg";
import LaraVonKolontajImage from "@/src/assets/img/employees/Lara_von_Kolontaj.jpg";
import MajaVonKutzschenbachImage from "@/src/assets/img/employees/Maja_von_Kutzschenbach.jpg";
import MelanieTreudeImage from "@/src/assets/img/employees/Melanie_Treude.jpg";
import MeralCitakImage from "@/src/assets/img/employees/Meral_Citak.jpg";
import MichaelBickImage from "@/src/assets/img/employees/Michael_Bick.jpg";
import SarahLongerichImage from "@/src/assets/img/employees/Sarah_Longerich.jpg";
import SebastianPleissImage from "@/src/assets/img/employees/Sebastian_Pleiss.jpg";
import SonjaHartmannImage from "@/src/assets/img/employees/Sonja_Hartmann.jpg";
import WingImage from "@/src/assets/img/jobs/wing.svg";
import Typography, { TypographyProps } from "@mui/material/Typography";
import PictureTeaserCard from "@/src/components/Cards/PictureTeaserCard/PictureTeaserCard";
import teamSectionStyle from "@/src/components/Sections/Generic/TeamSection/teamSectionStyle";
import { Box, Button, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Section from "@/src/components/Section/Section";

type TeamSectionProps = {
  headline?: string;
  subHeadline?: string;
  subHeadlineVariant?: TypographyProps["variant"];
  isSubsection?: boolean;
  showHeadings?: boolean;
};

type EmployeeItem = {
  title: string;
  subTitle?: string;
  image: string;
  isLightVariant?: boolean;
};

const TeamSection = ({ headline = "", subHeadline, subHeadlineVariant = "h4", isSubsection = false, showHeadings = true }: TeamSectionProps) => {
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);

  const employees: EmployeeItem[] = [
    {
      title: "Andreas Quauke",
      image: AndreasQuaukeImage,
    },
    {
      title: "Angela Feil",
      image: AngelaFeilImage,
    },
    {
      title: "Anna Beiten",
      image: AnnaBeitenImage,
    },
    {
      title: "Christopher Krause",
      image: ChristopherKrauseImage,
    },
    {
      title: "Dario Borning",
      image: DarioBorningImage,
    },
    {
      title: "Elenor Grub",
      image: ElenorGrubImage,
    },
    {
      title: "Frank Riedel",
      image: FrankRiedelImage,
    },
    {
      title: "Frederik Schier",
      image: FrederikSchierImage,
    },
    {
      title: "Harald Höveler",
      image: HaraldHoevelerImage,
    },
    {
      title: "Katharina Pitzen",
      image: KatharinaPitzenImage,
    },
    {
      title: "Kristina Beyer",
      image: KristinaBeyerImage,
    },
    {
      title: "Lara von Kolontaj",
      image: LaraVonKolontajImage,
    },
    {
      title: "Maja von Kutzschenbach",
      image: MajaVonKutzschenbachImage,
    },
    {
      title: "Melanie Treude",
      image: MelanieTreudeImage,
    },
    {
      title: "Meral Citak",
      image: MeralCitakImage,
    },
    {
      title: "Michael Bick",
      image: MichaelBickImage,
    },
    {
      title: "Sarah Longerich",
      image: SarahLongerichImage,
    },
    {
      title: "Sebastian Pleiß",
      image: SebastianPleissImage,
    },
    {
      title: "Sonja Hartmann",
      image: SonjaHartmannImage,
    },
    {
      title: "… und viele Weitere",
      subTitle: "sind jederzeit für Sie da.",
      image: WingImage,
      isLightVariant: true,
    },
  ];

  useEffect(() => {
    if (!showAllEmployees && toggleButtonRef.current) {
      toggleButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showAllEmployees]);

  return (
    <Section component={showHeadings ? "section" : "div"}>
      {showHeadings && (
        <Container variant={"xs"} showVerticalPadding={false} sx={{ paddingBottom: "3rem" }}>
          {!isSubsection && <Box id="partneranwaelte" />}
          <Typography component={"h2"} variant={"h3"} align={"center"}>
            {!isSubsection && headline}
          </Typography>
          <Typography component={"h3"} variant={subHeadlineVariant} align={"center"}>
            {isSubsection ? headline : subHeadline}
          </Typography>
        </Container>
      )}
      <Container variant={"xl"} showVerticalPadding={false}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          {employees.map((employee: EmployeeItem, index: number) => (
            <Grid key={employee.title} sx={{ display: { xs: index < 3 || showAllEmployees ? "block" : "none", md: "block" } }}>
              <PictureTeaserCard image={employee.image} title={employee.title} subTitle={employee.subTitle} isLightVariant={employee.isLightVariant} />
            </Grid>
          ))}
        </Grid>
        <Stack ref={toggleButtonRef} direction="row" sx={teamSectionStyle.buttonContainer}>
          <Button onClick={() => setShowAllEmployees((prev) => !prev)} endIcon={showAllEmployees ? <ExpandLess /> : <ExpandMore />}>
            {showAllEmployees ? "Weniger anzeigen" : "Mehr anzeigen"}
          </Button>
        </Stack>
      </Container>
    </Section>
  );
};

export default TeamSection;
